import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {Package, Truck, FileText, Clock, Phone, ChevronLeft, MapPin, Mail, PackageOpen} from 'lucide-react';
import settings from '@/settings';
import {buildAcuityScheduleUrl} from '@/modules/kits/utils/acuityScheduleUrl';

interface Props {
    menu?: string;
}

const TestKits: React.FC<Props> = ({menu}) => {
    const [currentMenu, setCurrentMenu] = useState(menu || 'main');
    const {user} = useAuth();
    const navigate = useNavigate();

    const openPublicDocument = (key: string) => {
        const url = new URL('/api/public-media', settings.apiURL);
        url.searchParams.set('bucket', 'public');
        url.searchParams.set('key', key);
        window.open(url.toString());
    };

    // Mock kit data for now
    const mockKitHistory = [
        {
            id: 1,
            kit_id: 'KIT123456',
            status: 'created',
            date: '2024-01-10',
            test_name: 'Basic Health Panel',
        },
    ];

    const MenuCard: React.FC<{icon: React.ReactNode; title: string; onClick: () => void}> = ({
        icon,
        title,
        onClick,
    }) => (
        <button
            onClick={onClick}
            className="flex flex-col items-center p-6 bg-mld-menu-background border border-mld-panel-border rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center group">
            <div className="text-mld-primary mb-4 group-hover:scale-110 transition-transform">{icon}</div>
            <span className="text-mld-text-primary font-medium">{title}</span>
        </button>
    );

    const InfoCard: React.FC<{children: React.ReactNode}> = ({children}) => (
        <div className="bg-mld-menu-background border border-mld-panel-border rounded-2xl shadow-sm p-6">{children}</div>
    );

    const BackButton: React.FC<{onClick: () => void}> = ({onClick}) => (
        <button
            onClick={onClick}
            className="flex items-center text-mld-primary hover:text-mld-primary/80 transition-colors mb-4">
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back
        </button>
    );

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {currentMenu === 'main' && (
                    <>
                        <PageHeader title="Test Kits" subtitle="Manage your test kits and get help with collection" />

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <MenuCard
                                icon={<Package className="h-12 w-12" />}
                                title="Activate a kit"
                                onClick={() => navigate('/test-kits/activate')}
                            />
                            <MenuCard
                                icon={<Truck className="h-12 w-12" />}
                                title="Return your kit"
                                onClick={() => setCurrentMenu('return')}
                            />
                            <MenuCard
                                icon={<FileText className="h-12 w-12" />}
                                title="Kit instructions"
                                onClick={() => setCurrentMenu('instructions')}
                            />
                            <MenuCard
                                icon={<Clock className="h-12 w-12" />}
                                title="Kit activation history"
                                onClick={() => setCurrentMenu('history')}
                            />
                            <MenuCard
                                icon={<Phone className="h-12 w-12" />}
                                title="Support"
                                onClick={() => setCurrentMenu('support')}
                            />
                        </div>
                    </>
                )}

                {currentMenu === 'return' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('main')} />
                        <PageHeader title="Return Your Kit" subtitle="How would you like to return your kit to us?" />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <MenuCard
                                icon={<Truck className="h-16 w-16" />}
                                title="Ship it - Available nationwide"
                                onClick={() => setCurrentMenu('ship')}
                            />
                            <MenuCard
                                icon={<MapPin className="h-16 w-16" />}
                                title="Local drop off - Dallas-Fort Worth"
                                onClick={() => setCurrentMenu('dropoff')}
                            />
                            <MenuCard
                                icon={<PackageOpen className="h-16 w-16" />}
                                title="Local pick up - Dallas-Fort Worth"
                                onClick={() => setCurrentMenu('pickup')}
                            />
                        </div>
                    </>
                )}

                {currentMenu === 'instructions' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('main')} />
                        <PageHeader
                            title="Kit Instructions"
                            subtitle="Collection method instructions and recently activated kits"
                        />

                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-semibold text-mld-text-primary mb-4">
                                    Collection Method Instructions
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <MenuCard
                                        icon={
                                            <div className="w-12 h-12 bg-mld-primary/10 rounded-lg flex items-center justify-center">
                                                QD
                                            </div>
                                        }
                                        title="Quick Draw"
                                        onClick={() => openPublicDocument('Quickdraw-instructions-2022.pdf')}
                                    />
                                    <MenuCard
                                        icon={
                                            <div className="w-12 h-12 bg-mld-primary/10 rounded-lg flex items-center justify-center">
                                                FP
                                            </div>
                                        }
                                        title="Finger Prick"
                                        onClick={() => openPublicDocument('Final Finger Prick Instructions.pdf')}
                                    />
                                    <MenuCard
                                        icon={
                                            <div className="w-12 h-12 bg-mld-primary/10 rounded-lg flex items-center justify-center">
                                                US
                                            </div>
                                        }
                                        title="Urine Sample"
                                        onClick={() => openPublicDocument('mld-urine-instructions-2022.pdf')}
                                    />
                                </div>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold text-mld-text-primary mb-4">
                                    Your recently activated kits
                                </h3>
                                {mockKitHistory.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-mld-muted-text">No recently activated kits</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {mockKitHistory
                                            .filter((kit: any) => kit.status?.toLowerCase() === 'created' && kit.kit_id)
                                            .map((kit: any) => (
                                                <div
                                                    key={kit.id}
                                                    className="bg-mld-menu-background border border-mld-panel-border rounded-2xl shadow-sm p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h4 className="font-semibold text-mld-text-primary">
                                                                {kit.test_name}
                                                            </h4>
                                                            <p className="text-sm text-mld-muted-text">
                                                                Kit ID: {kit.kit_id}
                                                            </p>
                                                        </div>
                                                        <div className="text-right text-sm text-mld-muted-text">
                                                            {new Date(kit.date).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {currentMenu === 'history' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('main')} />
                        <PageHeader title="Kit Activation History" subtitle="Your activated and completed kits" />

                        {mockKitHistory.length === 0 ? (
                            <div className="text-center py-8">
                                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-mld-muted-text">No kit activation history</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {mockKitHistory
                                    .filter((kit: any) => kit.kit_id)
                                    .map((kit: any) => (
                                        <div key={kit.id} className="bg-mld-menu-background border border-mld-panel-border rounded-2xl shadow-sm p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-semibold text-mld-text-primary">
                                                        {kit.test_name}
                                                    </h4>
                                                    <p className="text-sm text-mld-muted-text">Kit ID: {kit.kit_id}</p>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm font-medium text-mld-text-primary capitalize">
                                                        {kit.status}
                                                    </div>
                                                    <div className="text-sm text-mld-muted-text">
                                                        {new Date(kit.date).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </>
                )}

                {currentMenu === 'support' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('main')} />
                        <PageHeader title="Support" subtitle="Have questions about your kit? Reach out to us!" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InfoCard>
                                <div className="text-center">
                                    <Phone className="h-12 w-12 text-mld-primary mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-mld-text-primary mb-2">Call or text</h3>
                                    <p className="text-mld-text-primary">(877) 355 - 7978</p>
                                </div>
                            </InfoCard>
                            <InfoCard>
                                <div className="text-center">
                                    <Mail className="h-12 w-12 text-mld-primary mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-mld-text-primary mb-2">Email</h3>
                                    <p className="text-mld-text-primary">info@mylabsdirect.com</p>
                                </div>
                            </InfoCard>
                        </div>
                    </>
                )}

                {currentMenu === 'ship' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('return')} />
                        <PageHeader
                            title="Ship Your Kit"
                            subtitle="Use the shipping label included in your test kit to ship the sample back to us!"
                        />
                    </>
                )}

                {currentMenu === 'dropoff' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('return')} />
                        <PageHeader
                            title="Drop Off Your Kit"
                            subtitle="Drop your sample off at one of our convenient locations - Currently available in DFW only"
                        />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <InfoCard>
                                <h3 className="text-lg font-semibold text-mld-text-primary mb-2">Plano, TX</h3>
                                <p className="text-mld-text-primary mb-4">610 Coit Rd, Ste 200, Plano, TX 75075</p>
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1672.9185986601044!2d-96.7663925543167!3d33.00806741218437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c27d88f893443%3A0x12527c25944776dd!2sMy%20Labs%20Direct%20-%20Plano!5e0!3m2!1sen!2sus!4v1660769366452!5m2!1sen!2sus"
                                    width="100%"
                                    height="300"
                                    title="plano"
                                    className="border-0 rounded-lg mb-2"
                                    loading="lazy"
                                />
                                <p className="text-sm text-mld-muted-text">Available anytime</p>
                            </InfoCard>
                            <InfoCard>
                                <h3 className="text-lg font-semibold text-mld-text-primary mb-2">Farmers Branch, TX</h3>
                                <p className="text-mld-text-primary mb-4">
                                    4099 McEwen Rd #160 Farmers Branch, TX 75244
                                </p>
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.7727895819216!2d-96.84450078450008!3d32.93060078344497!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c272ecfa9830b%3A0x9fc547f3f3359d0f!2sMyLabsDirect%20-%20Farmers%20Branch!5e0!3m2!1sen!2sus!4v1660769535977!5m2!1sen!2sus"
                                    width="100%"
                                    height="300"
                                    title="farmersbranch"
                                    className="border-0 rounded-lg mb-2"
                                    loading="lazy"
                                />
                                <p className="text-sm text-mld-muted-text">Monday-Friday 9-5pm CST</p>
                            </InfoCard>
                        </div>
                    </>
                )}

                {currentMenu === 'pickup' && (
                    <>
                        <BackButton onClick={() => setCurrentMenu('return')} />
                        <PageHeader
                            title="Schedule Kit Pickup"
                            subtitle="Want us to pick your sample up? Currently available in DFW only"
                        />

                        <InfoCard>
                            <iframe
                                src={buildAcuityScheduleUrl(user?.userInfo ?? {})}
                                title="pickupappt"
                                className="w-full border-0 rounded-lg"
                                style={{height: '600px'}}
                            />
                        </InfoCard>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
};

export default TestKits;
